import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import { queryGet, queryUpdate } from './fetch';
import { ActionCausedBy } from './eventHistory';
import { ModeratedTaskFormat, TaskSetting } from './taskSetting';

export enum TaskType {
  MODERATED = 'MODERATED',
  UNMODERATED = 'UNMODERATED',
}

export enum TaskStatus {
  COMMITTED = 'COMMITTED',
  CANCELLED = 'CANCELLED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  VALIDATED = 'VALIDATED',
  REPORTED = 'REPORTED',
  REPORT_CONTESTED = 'REPORT_CONTESTED',
  DELETED = 'DELETED',
}

export enum ReportReason {
  NO_SHOW = 'NO_SHOW',
  PROFILE_LIE = 'PROFILE_LIE',
  CONNECTION_ISSUES = 'CONNECTION_ISSUES',
  TECHNICAL_REQUIREMENTS_ISSUES = 'TECHNICAL_REQUIREMENTS_ISSUES',
  BAD_SURROUNDINGS = 'BAD_SURROUNDINGS',
}

export interface Meeting {
  id: string;
  taskSettingId: string;
  status: string; // @TODO
  start: Date;
  end: Date;

  videoCallLink: string;
  address: string;
  instructions: string;
  format: ModeratedTaskFormat;
}

export interface Task {
  id: string;
  taskSetting: TaskSetting;
  taskSettingId: string;
  participationId: string;
  participantId: string;
  sessionId: string;
  type: TaskType;
  duration: number;
  durationDisplayed?: string;
  status: TaskStatus;
  sessionLinkId?: string;
  incentiveDisplayed?: string;
  cancelledReason?: string;
  instructions?: string;
  cancelledBy?: ActionCausedBy;
  reportReason?: ReportReason;
  meeting?: Meeting;
  format?: ModeratedTaskFormat;
  sessionPhoneNumber?: string;
  videoCallLink?: string;
}

export const useMarkTaskAsCompleted = (id?: string) => {
  return useMutation<Task, undefined, void>(
    queryUpdate(`/v2/tasks/${id}/completed`),
  );
};

export const useTask = (id?: string) => {
  return useQuery<Task>(['tasks', id], queryGet(`/v2/tasks/${id}`), {
    enabled: !!id,
  });
};

export const useTaskMeeting = (id?: string) => {
  return useQuery<Meeting>(
    ['tasks', id, 'meeting'],
    queryGet(`/v2/tasks/${id}/meeting`),
    {
      enabled: !!id,
    },
  );
};

export interface MeetingInstructionsDto {
  videoCallLink?: string | null;
  address?: string | null;
  instructions?: string | null;
}

export const useUpdateMeetingInstructions = (
  taskId?: string,
  options: UseMutationOptions<Meeting, Error, MeetingInstructionsDto> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<Meeting, Error, MeetingInstructionsDto>(
    queryUpdate(`/v2/tasks/${taskId}/meeting`),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        options.onSuccess?.(data, variables, context);
        queryClient.setQueryData(['tasks', taskId, 'meeting'], data);
      },
    },
  );
};
